// ─── token.js ───────────────────────────────────────────────────────────────
// Small helpers for the sign-in "token" saved in the browser.
//
// There are two separate sign-ins, each saved under its own name, so they can
// never get mixed up in the same browser:
//   'customer' → saved as 'token'   (homeowners, the dashboard)
//   'qc'       → saved as 'qcToken' (QC staff, the /qc pages)
//
// A token is a JWT: three parts separated by dots. The middle part holds
// readable details, including "exp" (when it expires, in seconds) and "sub"
// (who it belongs to: a phone number, or a QC username).

const STORAGE_KEYS = {
  customer: 'token',
  qc:       'qcToken',
};

export function getToken(area = 'customer') {
  try {
    return localStorage.getItem(STORAGE_KEYS[area]);
  } catch {
    return null; // e.g. browser storage is blocked
  }
}

export function setToken(token, area = 'customer') {
  localStorage.setItem(STORAGE_KEYS[area], token);
}

export function clearToken(area = 'customer') {
  try {
    localStorage.removeItem(STORAGE_KEYS[area]);
  } catch {
    // nothing to do
  }
}

// Reads the middle part of a JWT. Returns null if it can't be read.
export function readTokenDetails(token) {
  if (!token) return null;
  try {
    const middle = token.split('.')[1];
    // JWTs use "base64url": swap two characters so atob() can read it.
    const base64 = middle.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

// True if the token exists, can be read, and has not expired yet.
export function isTokenValid(token) {
  const details = readTokenDetails(token);
  if (!details) return false;
  return !details.exp || details.exp * 1000 > Date.now();
}

// The phone number (customer) or username (QC) inside the token.
export function getTokenSubject(area = 'customer') {
  return readTokenDetails(getToken(area))?.sub ?? '';
}
